/**
 * The campaign a visitor came from, handed to the Manager at every door into it.
 *
 * The Manager stamps these on the lead, the client (`initial_utm_*` and friends) and on every
 * server-side conversion (`conversion_utm_*`), but only ever reads them from the request that
 * creates the record — register, contact, a booked demo — and by then the visitor has clicked
 * through a page or two, or come back days later, and the query string is long gone.
 *
 * **Last tagged touch, kept 90 days** (ADR-0011). The store holds one touch: the parameters of one
 * landing and when that landing was first seen. A landing that carries any tracked parameter
 * replaces it as a whole; a landing without any never touches it; a touch older than 90 days is
 * dropped on read. That is the rule PostHog's Marketing analytics (`last_touch`, 90 days) and the
 * ad platforms' last click already apply, so the numbers can be reconciled.
 *
 * Nothing is invented — a visitor who never arrived through a tagged link sends none.
 */

/** One key, so a touch is written and replaced atomically. */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();const STORAGE_KEY = 'offsoo:attribution'
/** The v1 store: first touch of the session in `sessionStorage`, with its timestamp beside it. */
const LEGACY_SEEN_AT_KEY = 'offsoo:attribution:seen-at'

/** Marketing analytics' attribution window, and Google Ads' longest click window. */
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000

/**
 * URL parameter → the key the Manager's `ClientAttribution::fromRequest()` reads.
 *
 * This map and that method are the only place the two codebases agree on a name. A parameter
 * missing here is a Manager column that is silently always null, which is what happened to every
 * platform below Meta and TikTok until 2026-08-01.
 *
 * **All five UTMs, not three.** `utm_content` carries the creative and `utm_term` the placement
 * (Meta) or the matched keyword (search) — ADR 0038 in Creatives. posthog-js already stores all
 * five on the *person* through `save_campaign_params`; forwarding these two is what puts them on
 * the paying **client**, where MRR, plan and churn live. `utm_id` is deliberately absent: PostHog
 * does not store it and ADR 0038 dropped it from the convention.
 *
 * **Google is three keys, not one.** Since iOS 14.5 it sends `gbraid` (web-to-app) or `wbraid`
 * (app-to-web) *instead of* `gclid`, so a visitor carries exactly one of the three. Keeping only
 * `gclid` left every iOS click from Google Ads unattributable server-side.
 *
 * `msclkid` is Microsoft's, and the only key its server-side conversion import accepts — it has no
 * shared-event-id mechanism the way Meta does. `li_fat_id` is LinkedIn's first-party id.
 *
 * `twclid` (X) and `epik` (Pinterest) are captured into PostHog by `custom_campaign_params` but are
 * deliberately not forwarded: neither platform has a pixel configured here, so the Manager would be
 * storing something nothing reads.
 */
const PARAMS = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbclid: 'fbclid',
  ttclid: 'ttclid',
  msclkid: 'msclkid',
  li_fat_id: 'liFatId',
} as const

export type Attribution = Partial<Record<(typeof PARAMS)[keyof typeof PARAMS], string>>

/** The column is 255 wide; a longer value would be truncated server-side anyway. */
const LIMIT = 255

type Touch = { v: 2; params: Attribution; seenAt: number }

function isTouch(value: unknown): value is Touch {
  if (!value || typeof value !== 'object') {
    return false
  }
  const touch = value as Partial<Touch>
  return (
    touch.v === 2 &&
    typeof touch.seenAt === 'number' &&
    Number.isFinite(touch.seenAt) &&
    Boolean(touch.params) &&
    typeof touch.params === 'object'
  )
}

function write(touch: Touch): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(touch))
  } catch {
    // Not being able to store it is not worth failing a signup over.
  }
}

function remove(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing stored, then — the same outcome.
  }
}

/**
 * Moves a v1 session value across, so a visitor who is mid-session at deploy time keeps their
 * campaign. Only when there is nothing in `localStorage` yet; the old keys go either way.
 */
function migrate(): Touch | null {
  let touch: Touch | null = null
  try {
    const legacy = window.sessionStorage.getItem(STORAGE_KEY)
    if (!legacy) {
      return null
    }
    const params = JSON.parse(legacy) as unknown
    if (params && typeof params === 'object' && Object.keys(params).length > 0) {
      const seenAt = Number(window.sessionStorage.getItem(LEGACY_SEEN_AT_KEY)) || Date.now()
      touch = { v: 2, params: params as Attribution, seenAt }
    }
    window.sessionStorage.removeItem(STORAGE_KEY)
    window.sessionStorage.removeItem(LEGACY_SEEN_AT_KEY)
  } catch {
    // Private modes and storage-blocking extensions throw rather than return null.
    return null
  }
  if (touch) {
    write(touch)
  }
  return touch
}

/** The stored touch, or null when there is none, it is unreadable, or it is past the window. */
function read(): Touch | null {
  let stored: string | null
  try {
    stored = window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // Private modes and storage-blocking extensions throw rather than return null.
    return null
  }

  let touch: Touch | null = null
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as unknown
      touch = isTouch(parsed) ? parsed : null
    } catch {
      touch = null
    }
    if (!touch) {
      remove()
    }
  } else {
    touch = migrate()
  }

  if (touch && Date.now() - touch.seenAt > MAX_AGE_MS) {
    remove()
    return null
  }
  return touch
}

function sameParams(a: Attribution, b: Attribution): boolean {
  const keys = Object.keys(a) as (keyof Attribution)[]
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key])
}

/**
 * Remembers the campaign parameters in the current URL as the visitor's last tagged touch.
 *
 * Replaced as a set, never field by field: a `utm_campaign` from one click and a `gclid` from
 * another describe an ad nobody ran. An untagged landing — a direct or organic return — is the
 * effect of the earlier click, not a new source, so it leaves the touch alone. The same touch
 * landing twice (a reload of the campaign URL) keeps its `seenAt`, which Meta's `fbc` needs to be
 * the moment the `fbclid` was first observed.
 */
export function captureAttribution(search: string = typeof window === 'undefined' ? '' : window.location.search) {
  if (typeof window === 'undefined') {
    return
  }

  const params = new URLSearchParams(search)
  const found: Attribution = {}

  for (const [param, key] of Object.entries(PARAMS)) {
    const value = params.get(param)?.trim()
    if (value) {
      found[key] = value.slice(0, LIMIT)
    }
  }

  // Read first even when there is nothing to store: it migrates a v1 value and drops an expired one.
  const current = read()

  if (Object.keys(found).length === 0) {
    return
  }
  if (current && sameParams(current.params, found)) {
    return
  }

  write({ v: 2, params: found, seenAt: Date.now() })
}

/**
 * The ad click ids a server-side event needs, and when the touch that carried them was first seen.
 *
 * Meta lets a server rebuild `fbc` from `fbclid` when the pixel never wrote the `_fbc` cookie, but
 * the value carries a timestamp that has to be "when you first observed this fbclid" — the landing,
 * not the moment a later page posts the event. TikTok's `ttclid` carries no timestamp; it simply
 * stops matching seven days after the click. Empty when there is no stored touch.
 */
export function clickIdsForServerEvent(): { fbclid?: string; ttclid?: string; seenAt?: number } {
  if (typeof window === 'undefined') {
    return {}
  }
  const touch = read()
  if (!touch) {
    return {}
  }
  const { fbclid, ttclid } = touch.params

  return {
    ...(fbclid ? { fbclid } : {}),
    ...(ttclid ? { ttclid } : {}),
    seenAt: touch.seenAt,
  }
}

/**
 * The stored touch, in the keys the Manager reads, to be spread into the body of every request
 * that creates something there: register, contact, a booked demo. Empty when unknown.
 */
export function attributionForManager(): Attribution {
  return typeof window === 'undefined' ? {} : (read()?.params ?? {})
}

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

/**
 * The stored UTMs under their URL names, for a third party that takes them on its own link and
 * hands them back — Calendly stores them on the invitee and sends them in its webhook. No click
 * ids: those are for the ad platforms, and they reach them through the Manager.
 */
export function utmParameters(): Partial<Record<(typeof UTM_PARAMS)[number], string>> {
  const params = attributionForManager()
  const utms: Partial<Record<(typeof UTM_PARAMS)[number], string>> = {}
  for (const param of UTM_PARAMS) {
    const value = params[PARAMS[param]]
    if (value) {
      utms[param] = value
    }
  }
  return utms
}
